import React from "react";

import { capitalize, isEmpty, map } from "lodash/fp";

import { Stack, Typography } from "@mui/material";

import TableCell from "@app/analytics/components/Table/TableCell";
import TableRow from "@app/analytics/components/Table/TableRow";
import { getInitials } from "@app/common/utils";
import {
  EnterpriseRole,
  EnterpriseWorkspaceInvite,
} from "@app/common/workspaces/types";

import Avatar from "@rui/Avatar";
import Checkbox from "@rui/Checkbox";
import { Row } from "@rui/Stack";

import MenuItem from "../MenuItem";
import OptionsMenuCell from "../OptionsMenuCell";
import RoleCell from "../RoleCell";

const DEFAULT_ROLE: EnterpriseRole = "member";

interface MembersTableInviteRowProps {
  context: "team" | "workspace";
  isSelected: boolean;
  onCheckboxClick: (inviteId: string, type: "invite") => () => void;
  onMemberRemove: (id: string, type: "user" | "invite") => () => void;
  invite: EnterpriseWorkspaceInvite;
}

const MembersTableInviteRow = ({
  context,
  invite,
  isSelected,
  onCheckboxClick,
  onMemberRemove,
}: MembersTableInviteRowProps) => {
  return (
    <TableRow
      key={invite.id}
      sx={{
        bgcolor: isSelected ? "states.selected" : undefined,
      }}
    >
      <TableCell padding="checkbox">
        {context === "workspace" && (
          <Checkbox
            onClick={onCheckboxClick(invite.id, "invite")}
            checked={isSelected}
            inputProps={{
              "aria-labelledby": invite.id,
            }}
          />
        )}
      </TableCell>
      <TableCell>
        <Row variant="left">
          <Avatar sx={{ mr: 1 }}>{getInitials(invite.email)}</Avatar>
          <Stack>
            <Typography variant="bodyPrimaryReg" color="shades.neutral.800">
              {`(${capitalize(invite?.status ?? "")})`}
            </Typography>
            <Typography variant="bodySecondaryReg" color="shades.neutral.500">
              {invite.email}
            </Typography>
          </Stack>
        </Row>
      </TableCell>

      <RoleCell
        role={(invite.teamRole as EnterpriseRole) ?? DEFAULT_ROLE}
        disabled // only invite statuses are editable
      />
      {context === "workspace" && (
        <TableCell>
          {isEmpty(invite.teams) ? (
            "-"
          ) : (
            <Stack direction="row" alignItems="center">
              {map("name", invite.teams).join(", ")}
            </Stack>
          )}
        </TableCell>
      )}
      <OptionsMenuCell
        id="detail-menu"
        disabled={context !== "workspace"}
        menuItems={
          context === "workspace"
            ? [
                <MenuItem
                  key="remove-from-workspace"
                  onClick={onMemberRemove(invite.id, "invite")}
                >
                  Cancel pending invite
                </MenuItem>,
              ]
            : []
        }
      />
    </TableRow>
  );
};

export default MembersTableInviteRow;
